import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useInView, useScroll, useSpring, useTransform, type MotionValue } from 'framer-motion';
import { assetUrl, portfolioApi, sectionApi } from '../services/api';

interface PortfolioCard {
  id: string;
  title: string;
  category: string;
  image: string;
  slug: string;
  fullData: any;
}

interface SectionHeadingState {
  heading: string;
  description: string;
  button_text?: string | null;
  button_action?: 'modal' | 'link' | null;
  button_link?: string | null;
  layout_style?: 'style1' | 'style2' | 'style3' | 'style4' | 'style5';
}

interface BubbleCardProps {
  item: PortfolioCard;
  index: number;
  onClick: (item: PortfolioCard) => void;
  connectorToNextX?: number;
  showConnector?: boolean;
}

const normalizeLayoutStyle = (value: unknown): 'style1' | 'style2' | 'style3' | 'style4' | 'style5' => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'style2') return 'style2';
  if (raw === 'style3') return 'style3';
  if (raw === 'style4') return 'style4';
  if (raw === 'style5') return 'style5';
  return 'style1';
};

const BubbleCard: React.FC<BubbleCardProps> = ({ item, index, onClick, connectorToNextX = 0, showConnector = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const xOffset = (index % 3 - 1) * 80;
  const yMove = useTransform(scrollYProgress, [0, 1], [0, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.1, 0.82]);
  const rotate = useTransform(scrollYProgress, [0, 1], [index % 2 === 0 ? -16 : 16, index % 2 === 0 ? 16 : -16]);
  const opacity = useTransform(scrollYProgress, [0, 0.18, 0.82, 1], [0, 1, 1, 0]);
  const springScale = useSpring(scale, { stiffness: 95, damping: 28 });

  return (
    <motion.div
      ref={ref}
      style={{ y: yMove, scale: springScale, x: xOffset }}
      className="relative z-10"
    >
      <motion.button
        type="button"
        onClick={() => onClick(item)}
        style={{ rotate, opacity }}
        className="relative group cursor-pointer bg-transparent border-0 p-0"
        aria-label={`Open ${item.title}`}
      >
        <div className="w-56 h-56 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden border-2 border-white/10 bg-[#0a0a0a] shadow-[0_0_50px_rgba(255,107,53,0.12)] group-hover:border-[#FFBF00] transition-colors duration-500">
          <motion.img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full bg-[radial-gradient(circle_at_35%_30%,rgba(255,255,255,0.35),rgba(255,107,53,0.28)_40%,rgba(255,107,53,0.08)_65%,transparent_80%)]" />
          <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_90px_rgba(255,107,53,0.45)]" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          className="absolute -bottom-4 -right-2 md:-bottom-6 md:-right-8 bg-black/50 backdrop-blur-xl border border-white/15 p-4 md:p-5 rounded-full z-20 max-w-[260px] shadow-[0_10px_35px_rgba(0,0,0,0.45)] text-left"
        >
          <span className="text-[#FFBF00] text-xs md:text-sm font-bold tracking-[0.16em] mb-1 block">
            {item.category}
          </span>
          <h3 className="text-base md:text-xl font-semibold text-[#FFBF00] tracking-tight leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {item.title}
          </h3>
        </motion.div>

        <div className="absolute inset-[-18px] rounded-full border border-white/5 animate-[spin_20s_linear_infinite] pointer-events-none" />
        <div className="absolute inset-[-34px] rounded-full border border-white/[0.03] animate-[spin_30s_linear_infinite_reverse] pointer-events-none" />
      </motion.button>

      {showConnector && (
        <svg
          className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-[280px] md:w-[460px] h-[18rem] md:h-[24rem] pointer-events-none -z-10 overflow-visible"
          viewBox="0 0 460 420"
          fill="none"
          aria-hidden="true"
        >
          <path
            d={`M230 8 C230 ${132 + Math.abs(connectorToNextX) * 0.2}, ${230 + connectorToNextX} ${278 - Math.abs(connectorToNextX) * 0.08}, ${230 + connectorToNextX} 412`}
            stroke="rgba(255,255,255,0.22)"
            strokeWidth="2.4"
            strokeDasharray="6 10"
            strokeLinecap="round"
          />
          <path
            d={`M230 8 C230 ${132 + Math.abs(connectorToNextX) * 0.2}, ${230 + connectorToNextX} ${278 - Math.abs(connectorToNextX) * 0.08}, ${230 + connectorToNextX} 412`}
            stroke="rgba(255,107,53,0.34)"
            strokeWidth="3.6"
            strokeLinecap="round"
          />
        </svg>
      )}
    </motion.div>
  );
};

interface DepthCardProps {
  item: PortfolioCard;
  index: number;
  onClick: (item: PortfolioCard) => void;
}

const DepthCard: React.FC<DepthCardProps> = ({ item, index, onClick }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  const bgY = useTransform(smoothProgress, [0, 1], ['-10%', '10%']);
  const bgOpacity = useTransform(smoothProgress, [0, 0.5, 1], [0.1, 0.2, 0.1]);
  const cardY = useTransform(smoothProgress, [0, 1], ['15%', '-15%']);
  const cardRotate = useTransform(smoothProgress, [0, 1], [5, -5]);
  const opacity = useTransform(smoothProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <motion.section ref={sectionRef} className="relative h-screen w-full flex items-center justify-center overflow-hidden snap-start">
      <motion.div style={{ y: bgY, opacity: bgOpacity }} className="absolute inset-0 z-0">
        <img src={item.image} alt="" className="w-full h-full object-cover scale-150 blur-3xl grayscale" referrerPolicy="no-referrer" />
      </motion.div>

      <motion.div
        style={{ y: cardY, rotateY: cardRotate, opacity, perspective: 1000 }}
        className="relative z-10 w-[85vw] md:w-[50vw] aspect-[4/5] md:aspect-[16/10] rounded-3xl overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.5)] border border-white/10 group"
      >
        <motion.img
          initial={{ scale: 1.2 }}
          whileInView={{ scale: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-all duration-1000"
          referrerPolicy="no-referrer"
        />

        <div className="absolute bottom-12 left-12 right-12">
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="text-[#FFBF00] text-sm md:text-base font-bold tracking-[0.16em] mb-2 block"
          >
            {item.category}
          </motion.span>
          <motion.h3
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="text-4xl md:text-6xl lg:text-7xl font-semibold text-[#FFBF00] tracking-tight leading-[0.95] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {item.title}
          </motion.h3>
          <motion.div initial={{ width: 0 }} whileInView={{ width: '100%' }} transition={{ delay: 0.7, duration: 1 }} className="h-px bg-white/20 mb-6" />
          <div className="flex justify-end items-end">
            <button
              type="button"
              onClick={() => onClick(item)}
              className="w-12 h-12 rounded-full border border-black bg-black text-white flex items-center justify-center hover:border-[#FFBF00] hover:text-black transition-all group/btn overflow-hidden relative"
            >
              <span className="relative z-10">→</span>
              <div className="absolute inset-0 bg-[#FFBF00] translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </motion.div>

    </motion.section>
  );
};

interface StackedCardProps {
  item: PortfolioCard;
  index: number;
  progress: MotionValue<number>;
  total: number;
  onClick: (item: PortfolioCard) => void;
}

const StackedCard: React.FC<StackedCardProps> = ({ item, index, progress, total, onClick }) => {
  const start = index / Math.max(total, 1);
  const end = (index + 1) / Math.max(total, 1);
  const scale = useTransform(progress, [start, end], [1, 0.92]);
  const opacity = useTransform(progress, [start, end], [1, 0.45]);
  const y = useTransform(progress, [start, end], [0, -24]);
  const slideIn = useTransform(progress, [Math.max(0, start - 0.12), start], [180, 0]);
  const springScale = useSpring(scale, { stiffness: 100, damping: 30 });
  const springY = useSpring(y, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      style={{ scale: springScale, opacity, y: index === 0 ? springY : slideIn, zIndex: total - index }}
      className="absolute inset-0 flex items-center justify-center p-4 md:p-20"
    >
      <div className="relative w-full h-full max-w-6xl max-h-[700px] bg-[#0a0a0a] rounded-[2rem] md:rounded-[3rem] overflow-hidden border border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.8)] group">
        <div className="absolute inset-0 overflow-hidden">
          <motion.img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover transition-all duration-1000"
            referrerPolicy="no-referrer"
          />
        </div>

        <div className="absolute inset-0 p-8 md:p-20 flex flex-col justify-between z-10">
          <div />

          <div className="flex flex-col md:flex-row justify-between items-end gap-8">
            <div className="max-w-3xl">
              <span className="text-[#FFBF00] text-sm md:text-base font-bold tracking-[0.16em] mb-3 block">{item.category}</span>
              <h3 className="text-4xl md:text-6xl lg:text-7xl font-semibold text-[#FFBF00] tracking-tight leading-[0.95]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {item.title}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => onClick(item)}
              className="group/btn relative px-10 py-4 bg-black border border-black text-white font-black uppercase tracking-widest overflow-hidden rounded-full transition-transform hover:scale-105 active:scale-95 hover:border-[#FFBF00] hover:text-black whitespace-nowrap"
            >
              <span className="relative z-10">Explore the Project</span>
              <div className="absolute inset-0 bg-[#FFBF00] translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500" />
            </button>
          </div>
        </div>

        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FFBF00] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
      </div>
    </motion.div>
  );
};

export const SectionPortfolio: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLElement>(null);
  const style3ViewportRef = useRef<HTMLDivElement>(null);
  const style3TrackRef = useRef<HTMLDivElement>(null);
  const [portfolioCards, setPortfolioCards] = useState<PortfolioCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [style5ActiveIndex, setStyle5ActiveIndex] = useState(0);
  const [style3MaxTranslate, setStyle3MaxTranslate] = useState(0);
  const [style3HeightVh, setStyle3HeightVh] = useState(600);
  const [sectionHeading, setSectionHeading] = useState<SectionHeadingState>({
    heading: 'Selected Universe',
    description: '',
    button_text: 'Contact',
    button_action: 'modal',
    button_link: null,
    layout_style: 'style1',
  });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  const { scrollYProgress: style3Progress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });
  const bgRotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const bgRotateReverse = useTransform(scrollYProgress, [0, 1], [360, 0]);

  useEffect(() => {
    loadPortfolioCards();
    loadSectionHeading();
    const onCardsUpdate = () => loadPortfolioCards();
    const onHeadingUpdate = () => loadSectionHeading();
    window.addEventListener('portfolio:updated', onCardsUpdate);
    window.addEventListener('projects:updated', onHeadingUpdate);
    return () => {
      window.removeEventListener('portfolio:updated', onCardsUpdate);
      window.removeEventListener('projects:updated', onHeadingUpdate);
    };
  }, []);

  const loadPortfolioCards = async () => {
    try {
      setLoading(true);
      const response = await portfolioApi.getAll();
      const cards = Array.isArray(response?.data) ? response.data : [];
      const mappedCards = cards.map((card: any) => ({
        id: String(card.id),
        title: String(card.title || ''),
        category: String(card.category || 'Portfolio'),
        image: assetUrl(card.image_url || card.image),
        slug: String(card.slug || ''),
        fullData: card,
      }));
      setPortfolioCards(mappedCards.slice(0, 5));
    } catch (error) {
      console.error('Error loading portfolio cards:', error);
      setPortfolioCards([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSectionHeading = async () => {
    try {
      const response = await sectionApi.get('portfolio');
      if (response?.success && response.data) {
        setSectionHeading({
          heading: String(response.data.heading || 'Selected Universe'),
          description: String(response.data.description || ''),
          button_text: response.data.button_text || 'Contact',
          button_action: (response.data.button_action || 'modal') as 'modal' | 'link',
          button_link: response.data.button_link || null,
          layout_style: normalizeLayoutStyle(response.data.layout_style),
        });
      }
    } catch (error) {
      console.error('Error loading portfolio section heading:', error);
    }
  };

  const handleCardClick = (item: PortfolioCard) => {
    const slug = item.slug || item.fullData?.slug;
    if (slug) {
      navigate(`/portfolio/${slug}`);
    }
  };

  const handleCtaClick = () => {
    if (sectionHeading.button_action === 'link' && sectionHeading.button_link) {
      window.open(sectionHeading.button_link, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.href = '/contact';
  };

  const headingLines = String(sectionHeading.heading || '').split('\n').map((line) => line.trim()).filter(Boolean);
  const headingPrimary = headingLines[0] || 'Selected Universe';
  const headingSecondary = headingLines[1] || '';
  const isStyle2 = sectionHeading.layout_style === 'style2';
  const isStyle3 = sectionHeading.layout_style === 'style3';
  const isStyle4 = sectionHeading.layout_style === 'style4';
  const isStyle5 = sectionHeading.layout_style === 'style5';
  const style5HeightVh = Math.max(300, (portfolioCards.length + 1) * 100);
  const style3X = useTransform(style3Progress, [0, 1], [0, -style3MaxTranslate]);
  const style3SpringX = useSpring(style3X, { stiffness: 50, damping: 20, restDelta: 0.001 });
  const style3BgTextX = useTransform(style3Progress, [0, 1], ['10%', '-10%']);
  const style5AmbientX = useTransform(style3Progress, [0, 1], ['10%', '-10%']);
  const style5CurrentItem = portfolioCards[style5ActiveIndex] || null;

  useEffect(() => {
    if (!isStyle5) return;
    setStyle5ActiveIndex((prev) => Math.max(0, Math.min(prev, Math.max(0, portfolioCards.length - 1))));
  }, [isStyle5, portfolioCards.length]);
  useEffect(() => {
    if (!isStyle5 || portfolioCards.length === 0) return;
    const unsubscribe = style3Progress.on('change', (latest) => {
      const maxIndex = portfolioCards.length - 1;
      const next = Math.max(0, Math.min(maxIndex, Math.round(latest * maxIndex)));
      setStyle5ActiveIndex((prev) => (prev === next ? prev : next));
    });
    return () => unsubscribe();
  }, [isStyle5, portfolioCards.length, style3Progress]);

  useEffect(() => {
    if (!isStyle3) return;

    const recalcStyle3 = () => {
      const viewportEl = style3ViewportRef.current;
      const trackEl = style3TrackRef.current;
      if (!viewportEl || !trackEl) return;

      const viewportWidth = viewportEl.clientWidth || window.innerWidth || 1;
      const viewportHeight = window.innerHeight || 1;
      const trackWidth = trackEl.scrollWidth;
      const maxTranslate = Math.max(0, trackWidth - viewportWidth);
      setStyle3MaxTranslate(maxTranslate);

      // Extra buffer keeps the final slide fully visible before leaving the section.
      const requiredVh = 100 + ((maxTranslate + viewportHeight * 1.6) / viewportHeight) * 100;
      const safeVh = Number.isFinite(requiredVh) ? requiredVh : 420;
      setStyle3HeightVh(Math.max(320, Math.min(1200, Math.round(safeVh))));
    };

    recalcStyle3();
    window.addEventListener('resize', recalcStyle3);
    return () => window.removeEventListener('resize', recalcStyle3);
  }, [isStyle3, portfolioCards.length]);
  const canRenderStyle3 = isStyle3 && Number.isFinite(style3HeightVh) && style3HeightVh >= 100;

  if (loading) {
    return (
      <section className="relative pt-32 pb-24 bg-black flex items-center justify-center">
        <div className="text-white/70">Loading portfolio...</div>
      </section>
    );
  }

  return (
    <>
      {isStyle5 ? (
        <section className="relative bg-black" id="portfolio">
          <div className="container mx-auto px-6 pt-32 pb-16">
            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                className="text-4xl sm:text-6xl md:text-8xl font-black uppercase tracking-tight leading-[0.9]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {headingPrimary}
                {headingSecondary && (
                  <>
                    <br />
                    <span className="text-transparent" style={{ WebkitTextStroke: '1px white' }}>
                      {headingSecondary}
                    </span>
                  </>
                )}
              </motion.h2>
              {sectionHeading.description && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.35 }}
                  className="text-white/55 text-sm md:text-base max-w-2xl mx-auto mt-6"
                >
                  {sectionHeading.description}
                </motion.p>
              )}
            </div>
          </div>
          <section ref={containerRef} className="relative" style={{ height: `${style5HeightVh}vh` }}>
            <div className="sticky top-0 h-screen w-full overflow-hidden z-10">
            {portfolioCards.length > 0 && style5CurrentItem ? (
              <div className="relative w-full h-full flex items-center justify-center px-4 pt-24 pb-20 md:px-12 md:pt-28 md:pb-24 lg:px-20">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={style5CurrentItem.id}
                    initial={{ opacity: 0, y: 120, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -80, scale: 0.96 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    className="relative w-full h-full max-w-6xl max-h-[700px] bg-[#0a0a0a] rounded-[2rem] md:rounded-[3rem] overflow-hidden border border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.8)] group"
                  >
                    <div className="absolute inset-0 overflow-hidden">
                      <motion.img
                        src={style5CurrentItem.image}
                        alt="Portfolio project"
                        className="w-full h-full object-cover transition-all duration-1000"
                        referrerPolicy="no-referrer"
                      />
                    </div>

                    <div className="absolute inset-0 p-8 md:p-20 flex flex-col justify-between z-10">
                      <div className="max-w-4xl">
                        <motion.span
                          initial={{ opacity: 0, y: 24 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -18 }}
                          transition={{ duration: 0.35, ease: 'easeOut' }}
                          className="mb-4 block text-xs md:text-sm text-[#FFBF00] uppercase tracking-[0.45em]"
                        >
                          {style5CurrentItem.category}
                        </motion.span>
                        <motion.h2
                          initial={{ opacity: 0, y: 40 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -24 }}
                          transition={{ duration: 0.45, ease: 'easeOut' }}
                          className="text-4xl sm:text-6xl md:text-8xl font-black uppercase tracking-tight leading-[0.9] text-[#FFBF00]"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          {style5CurrentItem.title}
                        </motion.h2>
                      </div>

                      <div className="flex flex-col md:flex-row justify-between items-end gap-8">
                        <button
                          type="button"
                          onClick={() => handleCardClick(style5CurrentItem)}
                          className="group/btn relative px-10 py-4 bg-white text-black font-black uppercase tracking-widest overflow-hidden rounded-full transition-transform hover:scale-105 active:scale-95 whitespace-nowrap"
                        >
                          <span className="relative z-10">Explore the Project</span>
                          <div className="absolute inset-0 bg-[#FFBF00] translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500" />
                        </button>
                      </div>
                    </div>

                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FFBF00] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  </motion.div>
                </AnimatePresence>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center px-6">
                <div className="text-center py-20 border border-white/10 rounded-2xl bg-white/[0.02] w-full max-w-2xl">
                  <h3 className="text-2xl text-white mb-2">No Portfolio Items Found</h3>
                  <p className="text-white/55">Please add portfolio projects from dashboard.</p>
                </div>
              </div>
            )}

            <div className="absolute bottom-10 right-6 md:right-12 lg:right-20 z-40">
              <div className="flex gap-1">
                {portfolioCards.map((_, i) => (
                  <div key={i} className="h-1 w-12 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-[#FFBF00] transition-all duration-300"
                      style={{ width: i < style5ActiveIndex ? '100%' : i === style5ActiveIndex ? '65%' : '0%' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          </section>
        </section>
      ) : isStyle4 ? (
        <section ref={containerRef} className="relative bg-black" id="portfolio">
          <div className="container mx-auto px-6 pt-32 pb-16">
            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                className="text-4xl sm:text-6xl md:text-8xl font-black uppercase tracking-tight leading-[0.9]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {headingPrimary}
                {headingSecondary && (
                  <>
                    <br />
                    <span className="text-transparent" style={{ WebkitTextStroke: '1px white' }}>
                      {headingSecondary}
                    </span>
                  </>
                )}
              </motion.h2>
              {sectionHeading.description && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.35 }}
                  className="text-white/55 text-sm md:text-base max-w-2xl mx-auto mt-6"
                >
                  {sectionHeading.description}
                </motion.p>
              )}
            </div>
          </div>
          <div className="snap-y snap-mandatory">
            {portfolioCards.map((item, idx) => (
              <DepthCard key={item.id} item={item} index={idx} onClick={handleCardClick} />
            ))}
          </div>

        </section>
      ) : canRenderStyle3 ? (
        <section ref={containerRef} className="relative bg-black" style={{ height: `${style3HeightVh}vh` }} id="portfolio">
          <div className="container mx-auto px-6 pt-32 pb-8">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                className="text-4xl sm:text-6xl md:text-8xl font-black uppercase tracking-tight leading-[0.9]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {headingPrimary}
                {headingSecondary && (
                  <>
                    <br />
                    <span className="text-transparent" style={{ WebkitTextStroke: '1px white' }}>
                      {headingSecondary}
                    </span>
                  </>
                )}
              </motion.h2>
              {sectionHeading.description && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.35 }}
                  className="text-white/55 text-sm md:text-base max-w-2xl mx-auto mt-6"
                >
                  {sectionHeading.description}
                </motion.p>
              )}
            </div>
          </div>
          <div ref={style3ViewportRef} className="sticky top-0 h-screen w-full flex flex-col justify-center overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.02] select-none">
              <motion.h2 style={{ x: style3BgTextX }} className="text-[30vw] font-black uppercase whitespace-nowrap leading-none">
                Discovery Phase Discovery Phase
              </motion.h2>
            </div>

            {portfolioCards.length > 0 ? (
              <motion.div ref={style3TrackRef} style={{ x: style3SpringX }} className="flex items-center">
                {portfolioCards.map((item, idx) => (
                  <div key={item.id} className="relative h-screen w-[80vw] md:w-[60vw] flex-shrink-0 flex items-center justify-center px-12 md:px-24">
                    <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 pointer-events-none opacity-[0.03] select-none">
                      <span className="text-[40vw] font-black leading-none">{`0${idx + 1}`}</span>
                    </div>
                    <button type="button" onClick={() => handleCardClick(item)} className="relative w-full aspect-[16/10] md:aspect-[16/9] group text-left bg-transparent border-0 p-0">
                      <div className="absolute inset-0 overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]">
                        <motion.img
                          initial={{ scale: 1.2 }}
                          whileInView={{ scale: 1 }}
                          transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 ease-out"
                          referrerPolicy="no-referrer"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80 group-hover:opacity-40 transition-opacity duration-700" />
                      </div>
                      <div className="absolute -bottom-12 -left-8 md:-left-16 z-20 max-w-md">
                        <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                          <span className="text-[#FFBF00] text-sm md:text-base font-bold tracking-[0.16em] mb-4 block">{item.category} / {`0${idx + 1}`}</span>
                          <div className="flex items-center gap-6 overflow-hidden">
                            <motion.div initial={{ width: 0 }} whileInView={{ width: 60 }} className="h-[1px] bg-[#FFBF00]" />
                            <span className="text-[10px] text-white/40 uppercase font-bold tracking-[0.3em] whitespace-nowrap group-hover:text-white transition-colors">
                              View Project Detail
                            </span>
                          </div>
                        </motion.div>
                      </div>
                      <div className="absolute top-0 right-0 w-32 h-32 border-t border-r border-[#FFBF00]/20 group-hover:border-[#FFBF00]/60 transition-all duration-700 pointer-events-none" />
                    </button>
                  </div>
                ))}
                <div className="h-screen w-[100vw] flex-shrink-0 flex flex-col items-center justify-center text-center px-12">
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} className="max-w-4xl">
                    <h3 className="text-6xl md:text-[10vw] font-black uppercase tracking-tighter leading-none mb-12">
                      Let&apos;s Build <br />
                      <span className="text-[#FFBF00]">The Future</span>
                    </h3>
                    <button type="button" onClick={handleCtaClick} className="group relative px-16 py-6 bg-white text-black font-black uppercase tracking-widest overflow-hidden rounded-full transition-transform hover:scale-105 active:scale-95">
                      <span className="relative z-10">{sectionHeading.button_text || 'Start Project'}</span>
                      <div className="absolute inset-0 bg-[#FFBF00] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            ) : (
              <div className="px-6">
                <div className="text-center py-20 border border-white/10 rounded-2xl bg-white/[0.02]">
                  <h3 className="text-2xl text-white mb-2">No Portfolio Items Found</h3>
                  <p className="text-white/55">Please add portfolio projects from dashboard.</p>
                </div>
              </div>
            )}
          </div>
        </section>
      ) : isStyle2 || isStyle3 ? (
        <section ref={containerRef} className="relative pt-32 pb-24 bg-black" id="portfolio">
          <div className="container mx-auto px-6">
            <div className="text-center mb-24">
              <motion.h2
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.35 }}
                className="text-5xl md:text-7xl font-black uppercase"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {sectionHeading.heading || 'Our Exhibitions'}
              </motion.h2>

              {sectionHeading.description && (
                <p className="text-white/60 mt-6 max-w-xl mx-auto">
                  {sectionHeading.description}
                </p>
              )}
            </div>

            {portfolioCards.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 auto-rows-[320px]">
                {portfolioCards.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 80 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.6, delay: index * 0.08 }}
                    onClick={() => handleCardClick(item)}
                    className={`group relative overflow-hidden rounded-3xl cursor-pointer ${index % 5 === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition duration-700"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition duration-500" />
                    <div className="absolute bottom-8 left-8 opacity-0 group-hover:opacity-100 transition duration-500">
                      <p className="text-[#FFBF00] text-sm font-bold tracking-[0.16em]">
                        {item.category}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 border border-white/10 rounded-2xl bg-white/[0.02]">
                <h3 className="text-2xl text-white mb-2">No Portfolio Items Found</h3>
                <p className="text-white/55">Please add portfolio projects from dashboard.</p>
              </div>
            )}
          </div>
        </section>
      ) : (
        <section ref={containerRef} className="relative pt-32 pb-24 md:pt-48 md:pb-36 bg-black overflow-hidden" id="portfolio">
          <div className="absolute inset-0 pointer-events-none z-0">
            <motion.div
              style={{ rotate: bgRotate }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[155vw] h-[155vw] border border-white/[0.03] rounded-full"
            />
            <motion.div
              style={{ rotate: bgRotateReverse }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[105vw] h-[105vw] border border-white/[0.02] rounded-full"
            />
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="text-center mb-24 md:mb-36">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                className="text-4xl sm:text-6xl md:text-8xl font-black uppercase tracking-tight leading-[0.9]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {headingPrimary}
                {headingSecondary && (
                  <>
                    <br />
                    <span className="text-transparent" style={{ WebkitTextStroke: '1px white' }}>
                      {headingSecondary}
                    </span>
                  </>
                )}
              </motion.h2>
              {sectionHeading.description && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.35 }}
                  className="text-white/55 text-sm md:text-base max-w-2xl mx-auto mt-6"
                >
                  {sectionHeading.description}
                </motion.p>
              )}
            </div>

            {portfolioCards.length > 0 ? (
              <div className="flex flex-col items-center gap-28 md:gap-56">
                {portfolioCards.map((item, idx) => (
                  <BubbleCard
                    key={item.id}
                    item={item}
                    index={idx}
                    onClick={handleCardClick}
                    showConnector={idx < portfolioCards.length - 1}
                    connectorToNextX={((idx + 1) % 3 - (idx % 3)) * 80}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 border border-white/10 rounded-2xl bg-white/[0.02]">
                <h3 className="text-2xl text-white mb-2">No Portfolio Items Found</h3>
                <p className="text-white/55">Please add portfolio projects from dashboard.</p>
              </div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              className="mt-32 md:mt-52 flex flex-col items-center text-center"
            >
              <div className="relative w-24 h-24 md:w-32 md:h-32 flex items-center justify-center mb-10">
                <div className="absolute inset-0 border border-[#FFBF00] rounded-full animate-ping opacity-20" />
                <div className="absolute inset-4 border border-[#FFBF00] rounded-full animate-pulse" />
                <div className="w-3 h-3 md:w-4 md:h-4 bg-[#FFBF00] rounded-full" />
              </div>
              <h3 className="text-3xl md:text-6xl font-black uppercase tracking-tight mb-10 leading-[0.9]">
                Enter the <br /> Next Orbit
              </h3>
              <button
                type="button"
                onClick={handleCtaClick}
                className="group relative w-36 h-36 md:w-48 md:h-48 rounded-full border border-white/20 flex items-center justify-center overflow-hidden transition-all hover:border-[#FFBF00]"
              >
                <span className="relative z-10 font-black uppercase tracking-widest text-[10px] md:text-xs group-hover:text-black transition-colors">
                  {sectionHeading.button_text || 'Contact'}
                </span>
                <div className="absolute inset-0 bg-[#FFBF00] translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-full" />
              </button>
            </motion.div>
          </div>

          <div className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 hidden xl:block [writing-mode:vertical-rl]">
            <span className="text-[10px] font-mono text-white/10 uppercase tracking-[1em]">
              Orbital Visualization Engine / v2.0
            </span>
          </div>
        </section>
      )}

    </>
  );
};
